import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformSDKFloatToRESTFloat } from '@wix/sdk-runtime/transformations/float';
import { transformRESTFloatToSDKFloat } from '@wix/sdk-runtime/transformations/float';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformSDKFieldMaskToRESTFieldMask } from '@wix/sdk-runtime/transformations/field-mask';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveWixBillableItemsV1BillableItemsUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'manage._base_domain_': [
      {
        srcPath: '/_api/billable-items',
        destPath: '',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/billable-items/v1/billable-items',
        destPath: '/v1/billable-items',
      },
      {
        srcPath: '/billable-items/v1/bulk/billable-items',
        destPath: '/v1/bulk/billable-items',
      },
    ],
    _: [
      {
        srcPath: '/_api/billable-items',
        destPath: '',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/_api/billable-items',
        destPath: '',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_get-paid_billable-items';

/** Creates a billable item. */
export function createBillableItem(
  payload: object
): RequestOptionsFactory<any> {
  function __createBillableItem({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'billableItem.createdDate' },
          { path: 'billableItem.updatedDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.billable_items.v1.billable_item',
      method: 'POST' as any,
      methodFqn: 'wix.billable_items.v1.BillableItems.CreateBillableItem',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixBillableItemsV1BillableItemsUrl({
        protoPath: '/v1/billable-items',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'billableItem.createdDate' },
              { path: 'billableItem.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __createBillableItem;
}

/** Retrieves a billable item. */
export function getBillableItem(payload: object): RequestOptionsFactory<any> {
  function __getBillableItem({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.billable_items.v1.billable_item',
      method: 'GET' as any,
      methodFqn: 'wix.billable_items.v1.BillableItems.GetBillableItem',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixBillableItemsV1BillableItemsUrl({
        protoPath: '/v1/billable-items/{billableItemId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'billableItem.createdDate' },
              { path: 'billableItem.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getBillableItem;
}

/**
 * Updates a billable item.
 *
 *
 * Each time the BillableItem is updated,
 * `revision` increments by 1.
 * The current `revision` must be passed when updating the billable item.
 * This ensures you're working with the latest billable item
 * and prevents unintended overwrites.
 */
export function updateBillableItem(
  payload: object
): RequestOptionsFactory<any> {
  function __updateBillableItem({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'fieldMask' }],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'billableItem.createdDate' },
          { path: 'billableItem.updatedDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.billable_items.v1.billable_item',
      method: 'PATCH' as any,
      methodFqn: 'wix.billable_items.v1.BillableItems.UpdateBillableItem',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixBillableItemsV1BillableItemsUrl({
        protoPath: '/v1/billable-items/{billableItem.id}',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'billableItem.createdDate' },
              { path: 'billableItem.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __updateBillableItem;
}

/** Permanently deletes a billable item. */
export function deleteBillableItem(
  payload: object
): RequestOptionsFactory<any> {
  function __deleteBillableItem({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.billable_items.v1.billable_item',
      method: 'DELETE' as any,
      methodFqn: 'wix.billable_items.v1.BillableItems.DeleteBillableItem',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixBillableItemsV1BillableItemsUrl({
        protoPath: '/v1/billable-items/{billableItemId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __deleteBillableItem;
}

/**
 * Retrieves a list of BillableItems, given the provided [paging, filtering, and sorting](https://dev.wix.com/api/rest/getting-started/sorting-and-paging).
 *
 * Up to 1,000 BillableItems can be returned per request.
 *
 * To learn how to query BillableItems, see [API Query Language](https://dev.wix.com/api/rest/getting-started/api-query-language).
 */
export function queryBillableItems(
  payload: object
): RequestOptionsFactory<any> {
  function __queryBillableItems({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.billable_items.v1.billable_item',
      method: 'GET' as any,
      methodFqn: 'wix.billable_items.v1.BillableItems.QueryBillableItems',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixBillableItemsV1BillableItemsUrl({
        protoPath: '/v1/billable-items/query',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload, true),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'billableItems.createdDate' },
              { path: 'billableItems.updatedDate' },
            ],
          },
        ]),
      fallback: [
        {
          method: 'POST' as any,
          url: resolveWixBillableItemsV1BillableItemsUrl({
            protoPath: '/v1/billable-items/query',
            data: payload,
            host,
          }),
          data: payload,
        },
      ],
    };

    return metadata;
  }

  return __queryBillableItems;
}

/** Create multiple billable items. */
export function bulkCreateBillableItems(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkCreateBillableItems({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'billableItems.createdDate' },
          { path: 'billableItems.updatedDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.billable_items.v1.billable_item',
      method: 'POST' as any,
      methodFqn: 'wix.billable_items.v1.BillableItems.BulkCreateBillableItems',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixBillableItemsV1BillableItemsUrl({
        protoPath: '/v1/bulk/billable-items/create',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'results.item.createdDate' },
              { path: 'results.item.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __bulkCreateBillableItems;
}

/** Updates multiple billable items. */
export function bulkUpdateBillableItems(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkUpdateBillableItems({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'billableItems.fieldMask' }],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'billableItems.billableItem.createdDate' },
          { path: 'billableItems.billableItem.updatedDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.billable_items.v1.billable_item',
      method: 'POST' as any,
      methodFqn: 'wix.billable_items.v1.BillableItems.BulkUpdateBillableItems',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixBillableItemsV1BillableItemsUrl({
        protoPath: '/v1/bulk/billable-items/update',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'results.item.createdDate' },
              { path: 'results.item.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __bulkUpdateBillableItems;
}

/** Delete multiple billable items. */
export function bulkDeleteBillableItems(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkDeleteBillableItems({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.billable_items.v1.billable_item',
      method: 'POST' as any,
      methodFqn: 'wix.billable_items.v1.BillableItems.BulkDeleteBillableItems',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixBillableItemsV1BillableItemsUrl({
        protoPath: '/v1/bulk/billable-items/delete',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __bulkDeleteBillableItems;
}

/**
 * Updates tags for multiple billable items.
 * A tag that appears both in the list of assign and unassign tags, will be assigned.
 */
export function bulkUpdateBillableItemTags(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkUpdateBillableItemTags({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.billable_items.v1.billable_item',
      method: 'POST' as any,
      methodFqn:
        'wix.billable_items.v1.BillableItems.BulkUpdateBillableItemTags',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixBillableItemsV1BillableItemsUrl({
        protoPath: '/v1/bulk/billable-items/update-tags',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __bulkUpdateBillableItemTags;
}

/**
 * Updates tags on multiple billable items by filter. Works asynchronously, and returns a job ID.
 *
 * An empty filter will update all billable items.
 *
 * A tag that is specified both in the list of assign and unassign tags, will be assigned.
 *
 * Specify the returned jobId in Get Async Job ([SDK](https://dev.wix.com/docs/sdk/backend-modules/async-jobs/get-async-job) | [REST](https://dev.wix.com/docs/rest/business-management/async-job/get-async-job)) to track the job's status.
 */
export function bulkUpdateBillableItemTagsByFilter(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkUpdateBillableItemTagsByFilter({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.billable_items.v1.billable_item',
      method: 'POST' as any,
      methodFqn:
        'wix.billable_items.v1.BillableItems.BulkUpdateBillableItemTagsByFilter',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixBillableItemsV1BillableItemsUrl({
        protoPath: '/v1/bulk/billable-items/update-tags-by-filter',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __bulkUpdateBillableItemTagsByFilter;
}

/**
 * Retrieves a list of up to 100 billable items, given the specified filtering, search expression, sorting, and cursor paging.
 *
 * Learn more about [API query language](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-the-wix-api-query-language).
 */
export function searchBillableItems(
  payload: object
): RequestOptionsFactory<any> {
  function __searchBillableItems({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'search.aggregations.range.buckets.from' },
          { path: 'search.aggregations.range.buckets.to' },
          {
            path: 'search.aggregations.nested.nestedAggregations.range.buckets.from',
          },
          {
            path: 'search.aggregations.nested.nestedAggregations.range.buckets.to',
          },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.billable_items.v1.billable_item',
      method: 'POST' as any,
      methodFqn: 'wix.billable_items.v1.BillableItems.SearchBillableItems',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixBillableItemsV1BillableItemsUrl({
        protoPath: '/v1/billable-items/search',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'billableItems.createdDate' },
              { path: 'billableItems.updatedDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'aggregationData.results.ranges.results.from' },
              { path: 'aggregationData.results.ranges.results.to' },
              {
                path: 'aggregationData.results.groupedByValue.results.nestedResults.ranges.results.from',
              },
              {
                path: 'aggregationData.results.groupedByValue.results.nestedResults.ranges.results.to',
              },
              {
                path: 'aggregationData.results.nested.results.results.*.range.from',
              },
              {
                path: 'aggregationData.results.nested.results.results.*.range.to',
              },
              { path: 'aggregationData.results.scalar.value' },
              {
                path: 'aggregationData.results.groupedByValue.results.nestedResults.scalar.value',
              },
              {
                path: 'aggregationData.results.nested.results.results.*.scalar.value',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __searchBillableItems;
}
